/**
 * JL Expanse Integration Module
 * Bridges Justified Laboratories subscription functionality with Expanse theme events
 *
 * @version 1.0.0
 * @requires window.JL - JL Utilities library
 * @requires window.JLSubscriptionCore - JL Subscription Core module
 *
 * EXPANSE EVENTS HANDLED:
 * - page:loaded - Initialize module
 * - product-form:submit:before - Add selling_plan to FormData for subscriptions
 * - product-form:cart:navigate - Prevent redirect, open cart drawer instead
 * - cart:updated - Update cart count displays
 *
 * CRITICAL: This module ensures selling_plan is properly included in
 * subscription cart adds for Recharge integration.
 */

(function(global) {
  'use strict';

  // ============================================
  // DEPENDENCY CHECK
  // ============================================

  if (!global.JL) {
    console.error('[JL-EXPANSE] JL utilities library not found. Module cannot initialize.');
    return;
  }

  // ============================================
  // MODULE STATE
  // ============================================

  let _initialized = false;

  // ============================================
  // CONFIGURATION
  // ============================================

  const CONFIG = {
    // Selectors for purchase option detection
    selectors: {
      purchaseOptionRadio: 'input[name="purchase_option"]',
      subscriptionRadio: 'input[name="purchase_option"][value="subscription"]',
      sellingPlanInput: '#subs_seller_planId',
      sellingPlanSelect: '#jl-selling-plan',
      cartCount: '[data-cart-count]',
      cartCountLegacy: '#CartCount span'
    },
    // localStorage keys
    storage: {
      productType: 'product_type'
    },
    // Values
    values: {
      subscription: 'subscription',
      oneTime: 'one-time'
    }
  };

  // ============================================
  // SUBSCRIPTION DETECTION
  // ============================================

  /**
   * Check if subscription purchase option is currently selected
   * Checks multiple sources: radio buttons, localStorage, active tab
   * @returns {boolean} True if subscription is selected
   */
  function isSubscriptionSelected() {
    // Check radio button first (most reliable)
    const subscriptionRadio = document.querySelector(CONFIG.selectors.subscriptionRadio);
    if (subscriptionRadio && subscriptionRadio.checked) {
      console.log('[JL-EXPANSE] Subscription detected via radio button');
      return true;
    }

    // Check if one-time is explicitly selected
    const onetimeRadio = document.querySelector('input[name="purchase_option"][value="onetime"]');
    if (onetimeRadio && onetimeRadio.checked) {
      return false;
    }

    // Fall back to localStorage
    try {
      const productType = localStorage.getItem(CONFIG.storage.productType);
      if (productType === CONFIG.values.subscription) {
        console.log('[JL-EXPANSE] Subscription detected via localStorage');
        return true;
      }
    } catch (e) {
      console.warn('[JL-EXPANSE] localStorage not available:', e.message);
    }

    // Check active tab (legacy support)
    const activeTab = document.querySelector('.listing_link.active_tab');
    if (activeTab) {
      const dataVal = activeTab.getAttribute('data-val');
      if (dataVal !== CONFIG.values.oneTime) {
        console.log('[JL-EXPANSE] Subscription detected via active tab');
        return true;
      }
    }

    return false;
  }

  /**
   * Get the currently selected selling plan ID
   * @returns {string|null} Selling plan ID or null
   */
  function getSelectedSellingPlan() {
    // Try hidden input first (populated by subscription selection)
    const hiddenInput = document.querySelector(CONFIG.selectors.sellingPlanInput);
    if (hiddenInput && hiddenInput.value) {
      console.log('[JL-EXPANSE] Selling plan from hidden input:', hiddenInput.value);
      return hiddenInput.value;
    }

    // Try select element
    const selectEl = document.querySelector(CONFIG.selectors.sellingPlanSelect);
    if (selectEl && selectEl.value) {
      console.log('[JL-EXPANSE] Selling plan from select:', selectEl.value);
      return selectEl.value;
    }

    // Try global override (set by 30/60/90 module)
    if (global.JL_SELLING_PLAN_OVERRIDE) {
      console.log('[JL-EXPANSE] Selling plan from override:', global.JL_SELLING_PLAN_OVERRIDE);
      return global.JL_SELLING_PLAN_OVERRIDE;
    }

    console.warn('[JL-EXPANSE] No selling plan found');
    return null;
  }

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Handle product-form:submit:before event
   * Adds selling_plan to FormData for subscription purchases
   * CRITICAL: This ensures Recharge subscriptions work correctly
   *
   * @param {CustomEvent} event - Expanse form submit before event
   */
  function handleFormSubmitBefore(event) {
    console.log('[JL-EXPANSE] Handling product-form:submit:before');

    if (!event.detail || !event.detail.form) {
      console.warn('[JL-EXPANSE] No form data in event');
      return;
    }

    const formData = event.detail.form;

    // Check if selling_plan is already present (don't override)
    if (formData.has('selling_plan')) {
      console.log('[JL-EXPANSE] selling_plan already in FormData, skipping');
      return;
    }

    // Check if this is a subscription purchase
    if (!isSubscriptionSelected()) {
      console.log('[JL-EXPANSE] One-time purchase, not adding selling_plan');
      return;
    }

    // Get and add selling_plan
    const sellingPlanId = getSelectedSellingPlan();
    if (sellingPlanId) {
      formData.set('selling_plan', sellingPlanId);
      console.log('[JL-EXPANSE] Added selling_plan to FormData:', sellingPlanId);
    } else {
      console.error('[JL-EXPANSE] CRITICAL: Subscription selected but no selling_plan found!');
    }
  }

  /**
   * Handle product-form:cart:navigate event
   * Prevents page redirect and opens cart drawer instead (when applicable)
   *
   * @param {CustomEvent} event - Expanse cart navigate event
   */
  function handleCartNavigate(event) {
    console.log('[JL-EXPANSE] Handling product-form:cart:navigate');

    // Check cart type setting
    const cartType = document.body.dataset.cartType || document.body.getAttribute('data-cart-type');

    if (cartType === 'dropdown') {
      console.log('[JL-EXPANSE] Cart type is dropdown, preventing navigation');
      event.preventDefault();

      // Dispatch cart:open event for Expanse cart drawer
      document.dispatchEvent(new CustomEvent('cart:open', {
        bubbles: true
      }));

      console.log('[JL-EXPANSE] Dispatched cart:open event');
    } else {
      console.log('[JL-EXPANSE] Cart type is page, allowing navigation');
    }
  }

  /**
   * Handle cart:updated event
   * Updates cart count displays throughout the page
   *
   * @param {CustomEvent} event - Expanse cart updated event
   */
  function handleCartUpdated(event) {
    console.log('[JL-EXPANSE] Handling cart:updated');

    // Safely extract cart data
    const cart = event.detail?.cart;
    if (!cart) {
      console.warn('[JL-EXPANSE] No cart data in event');
      return;
    }

    const itemCount = cart.item_count;
    console.log('[JL-EXPANSE] Cart item count:', itemCount);

    // Update data-cart-count elements
    const cartCountEls = document.querySelectorAll(CONFIG.selectors.cartCount);
    cartCountEls.forEach(el => {
      el.textContent = itemCount.toString();
    });

    // Update legacy #CartCount span
    const legacyCartCount = document.querySelector(CONFIG.selectors.cartCountLegacy);
    if (legacyCartCount) {
      legacyCartCount.textContent = itemCount.toString();
    }

    console.log('[JL-EXPANSE] Cart count updated');
  }

  // ============================================
  // EVENT REGISTRATION
  // ============================================

  /**
   * Register all Expanse event handlers
   */
  function registerEventHandlers() {
    console.log('[JL-EXPANSE] Registering event handlers');

    // product-form:submit:before - Add selling_plan
    document.addEventListener('product-form:submit:before', handleFormSubmitBefore);
    console.log('[JL-EXPANSE] Registered: product-form:submit:before');

    // product-form:cart:navigate - Open cart drawer
    document.addEventListener('product-form:cart:navigate', handleCartNavigate);
    console.log('[JL-EXPANSE] Registered: product-form:cart:navigate');

    // cart:updated - Update cart count
    document.addEventListener('cart:updated', handleCartUpdated);
    console.log('[JL-EXPANSE] Registered: cart:updated');

    // Also listen for ajaxProduct:added for legacy compatibility
    document.addEventListener('ajaxProduct:added', function(event) {
      console.log('[JL-EXPANSE] ajaxProduct:added received');
      // Cart drawer will be handled by Expanse theme
    });

    console.log('[JL-EXPANSE] All event handlers registered');
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize the Expanse integration module
   */
  function init() {
    console.log('[JL-EXPANSE] Initializing Expanse integration module...');

    // Prevent re-initialization
    if (_initialized) {
      console.log('[JL-EXPANSE] Already initialized, skipping');
      return;
    }

    // Register event handlers
    registerEventHandlers();

    // Register for page:loaded event (Expanse specific)
    document.addEventListener('page:loaded', function() {
      console.log('[JL-EXPANSE] page:loaded event received');

      // Initialize JLSubscriptionCore if not already done
      if (global.JLSubscriptionCore && !global.JLSubscriptionCore._initialized) {
        global.JLSubscriptionCore.init();
      }
    });

    _initialized = true;
    console.log('[JL-EXPANSE] Expanse integration module initialized successfully');
  }

  // ============================================
  // PUBLIC API
  // ============================================

  const JLExpanseIntegration = {
    // State
    get _initialized() { return _initialized; },
    set _initialized(val) { _initialized = val; },

    // Initialization
    init,
    registerEventHandlers,

    // Event handlers (exposed for testing)
    handleFormSubmitBefore,
    handleCartNavigate,
    handleCartUpdated,

    // Utility functions
    isSubscriptionSelected,
    getSelectedSellingPlan,

    // Configuration (read-only)
    CONFIG
  };

  // Export to window
  global.JLExpanseIntegration = JLExpanseIntegration;

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, wait for next tick to allow other scripts to load
    setTimeout(init, 0);
  }

  console.log('[JL-EXPANSE] Module loaded and exported to window.JLExpanseIntegration');

})(typeof window !== 'undefined' ? window : this);
